import React from "react";
import { Box, Container, Heading, Text, SimpleGrid, VStack, HStack, Icon, Badge, Table, Thead, Tbody, Tr, Th, Td } from "@chakra-ui/react";
import { FaSolarPanel, FaChartLine, FaBolt, FaLeaf, FaArrowUp, FaDollarSign } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const PropertyStrategySolarValueAdd: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/strategy/solar-value-add"
        fallbackTitle="Solar Value-Add Strategy"
        fallbackDescription="Increase property value and returns with solar installations"
      />
      <NavbarWithCallToAction />

      <Box bgGradient="linear(to-r, orange.400, orange.600)" color="white" py={20}>
        <Container maxW="container.xl">
          <HStack spacing={12} align="center">
            <VStack align="flex-start" spacing={6} flex={1}>
              <Badge bg="orange.300" px={4} py={2} borderRadius="full">Value-Add Strategy</Badge>
              <Heading size="3xl" fontWeight="black">Solar Value-Add Strategy</Heading>
              <Text fontSize="xl" lineHeight="1.8">
                Boost property value and rental yields with solar installations. Sustainable investing that pays dividends.
              </Text>
            </VStack>
            <Box bg="whiteAlpha.200" p={8} borderRadius="2xl" display={{ base: "none", md: "block" }}>
              <Icon as={FaSolarPanel} boxSize={24} />
            </Box>
          </HStack>
        </Container>
      </Box>

      <Container maxW="container.xl" py={16}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl">ROI Comparison</Heading>
          <Text fontSize="lg" color="gray.600">Property with solar vs. without solar</Text>
        </VStack>

        <Box bg="white" borderRadius="xl" border="1px" borderColor="gray.200" overflow="hidden">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Metric</Th>
                <Th>Without Solar</Th>
                <Th color="orange.600">With Solar</Th>
                <Th>Difference</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td fontWeight="bold">Property Value</Td>
                <Td>$550,000</Td>
                <Td fontWeight="bold" color="orange.600">$575,000</Td>
                <Td color="green.600">+$25,000 (4.5%)</Td>
              </Tr>
              <Tr bg="gray.50">
                <Td fontWeight="bold">Weekly Rent</Td>
                <Td>$520</Td>
                <Td fontWeight="bold" color="orange.600">$560</Td>
                <Td color="green.600">+$40 (7.7%)</Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Annual Yield</Td>
                <Td>4.9%</Td>
                <Td fontWeight="bold" color="orange.600">5.4%</Td>
                <Td color="green.600">+0.5%</Td>
              </Tr>
              <Tr bg="gray.50">
                <Td fontWeight="bold">Tenant Appeal</Td>
                <Td>Standard</Td>
                <Td fontWeight="bold" color="orange.600">Premium</Td>
                <Td color="green.600">High demand</Td>
              </Tr>
              <Tr>
                <Td fontWeight="bold">Time to Lease</Td>
                <Td>21 days</Td>
                <Td fontWeight="bold" color="orange.600">12 days</Td>
                <Td color="green.600">-9 days (43%)</Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>
      </Container>

      <Box bg="orange.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Heading size="xl">Solar Value Benefits</Heading>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            <Box bg="white" p={6} borderRadius="xl" textAlign="center">
              <Icon as={FaArrowUp} boxSize={10} color="orange.500" mb={4} />
              <Heading size="sm" mb={2}>Increased Value</Heading>
              <Text fontSize="sm" color="gray.600">3-5% property value boost</Text>
            </Box>
            <Box bg="white" p={6} borderRadius="xl" textAlign="center">
              <Icon as={FaDollarSign} boxSize={10} color="green.500" mb={4} />
              <Heading size="sm" mb={2}>Higher Rent</Heading>
              <Text fontSize="sm" color="gray.600">Premium rental returns</Text>
            </Box>
            <Box bg="white" p={6} borderRadius="xl" textAlign="center">
              <Icon as={FaBolt} boxSize={10} color="blue.500" mb={4} />
              <Heading size="sm" mb={2}>Lower Costs</Heading>
              <Text fontSize="sm" color="gray.600">Reduced energy bills</Text>
            </Box>
            <Box bg="white" p={6} borderRadius="xl" textAlign="center">
              <Icon as={FaLeaf} boxSize={10} color="teal.500" mb={4} />
              <Heading size="sm" mb={2}>Sustainability</Heading>
              <Text fontSize="sm" color="gray.600">Eco-friendly appeal</Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      <Box bg="orange.600" color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaSolarPanel} boxSize={16} />
            <Heading size="xl">Add Solar to Your Investment</Heading>
            <Text fontSize="lg">Explore our Property + Solar package</Text>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyStrategySolarValueAdd;
